import * as React from 'react';
import { isEmpty, isNil } from 'lodash';
import classnames from 'classnames';

import Spinner from 'components/spinner/Spinner';
import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Popover from 'components/popover/Popover';
import ActionButton from 'components/buttons/action-button/ActionButton';
import EmptyMessage from 'modules/reporting/components/empty-message/EmptyMessage';

import { __t, applyTranslations } from 'utils/language';
import { getMetricValueInPercentage } from 'utils/helper/common';

import type { ReportingStatisticTableWithBenchmarkItem } from '../../types/ReportingStatisticTableWithBenchmarkItem.type';

import './reporting-statistics-table-with-benchmark.scss';

applyTranslations(require('./reporting-statistics-table-with-benchmark.i18n.json'));

type Props = {
  statisticsItems: Array<ReportingStatisticTableWithBenchmarkItem>,
  campaignId?: number,
  isLoading: boolean,
  wrapperClass?: string
};

const ReportingStatisticsTableWithBenchmark = (props: Props): JSX.Element => {
  const { statisticsItems, campaignId, wrapperClass, isLoading } = props;

  const hasBenchmarks = statisticsItems.some(item => item.benchmarkValue !== undefined);
  const containerClassNames = classnames('reporting-statistics-table-with-benchmark', wrapperClass);
  const itemClassNames = classnames('reporting-statistics-table-with-benchmark__metric', {
    'reporting-statistics-table-with-benchmark__metric--single-column': !hasBenchmarks
  });


  const renderSeparator = (): React.ReactNode => (
    <div className="reporting-statistics-table-with-benchmark__metric-seperator" />
  );

  const renderName = (
    item: ReportingStatisticTableWithBenchmarkItem
  ): React.ReactNode => (
    <div className="reporting-statistics-table-with-benchmark__metric-name">
      {item.label}
      {
        item.tooltip && (
          <Tooltip
            message={item.tooltip}
            width="long"
            showQuestionMark
            classNameWrapper="reporting-statistics-table-with-benchmark__tooltip"
          />
        )
      }
    </div>
  );

  const renderMainValue = (
    item: ReportingStatisticTableWithBenchmarkItem
  ): React.ReactNode => (
    <div className="reporting-statistics-table-with-benchmark__metric-value">
      {item.value}
    </div>
  );

  const renderBenchmarkValue = (
    item: ReportingStatisticTableWithBenchmarkItem
  ): React.ReactNode => (
    <div>
      <div className="reporting-statistics-table-with-benchmark__metric-value-benchmark">
        {
          !isNil(item.deltaValue) && !isNil(item.benchmarkValue) && (
            <div className="reporting-statistics-table-with-benchmark__benchmark-compare-container">
              <SVGDynamicIcon
                name={item.deltaValue < 0 ? 'triangle-down-custom-bg' : 'triangle-up-custom-bg'}
                width={8}
                height={5}
                customColor={item.deltaValue < 0 ? 'red' : 'primary'}
              />
              <span
                className="reporting-statistics-table-with-benchmark__benchmark-compare-value"
                style={item.deltaValue < 0 ? { color: '#d61600' } : {}}
              >
                {getMetricValueInPercentage(Math.abs(item.deltaValue))}
              </span>
            </div>
          )
        }
        <div className="reporting-statistics-table-with-benchmark__benchmark-actual-value">
          {item.benchmarkValue || 'n/a'}
        </div>
      </div>
      <div className="reporting-statistics-table-with-benchmark__benchmark-label">
        {__t('reporting_statistics_table_with_benchmark.industry_benchmark_label')}
      </div>
    </div>
  );

  const renderPopoverContents = (): React.ReactNode => (
    <React.Fragment>
      <div className="reporting-statistics-table-with-benchmark__note">
        {__t('reporting_statistics_table_with_benchmark.cost_distribution_note')}
      </div>
      {
        campaignId && (
          <ActionButton
            link={`/campaigns/${campaignId}/report/v2/cca`}
          >
            {__t('reporting_statistics_table_with_benchmark.cca_button')}
          </ActionButton>
        )
      }
    </React.Fragment>
  );

  const renderPopover = (): React.ReactNode => (
    <Popover
      classNamePopover="reporting-statistics-table-with-benchmark__cost-distribution-popover"
      classNameWrapper="reporting-statistics-table-with-benchmark__popover-wrapper"
      triggerComponent={
        <SVGDynamicIcon
          height={24}
          width={24}
          customColor="red"
          name="exclamation-inside-circle-custom-bg-white-fg"
        />}
    >
      {renderPopoverContents()}
    </Popover>
  );

  const renderItem = (
    item: ReportingStatisticTableWithBenchmarkItem
  ): React.ReactNode => (
    <div
      key={item.key}
      className={itemClassNames}
    >
      {!hasBenchmarks && (<div />)}
      <div className="reporting-statistics-table-with-benchmark__metric-actual">
        {
          item.hasDefinedAttribution === false
            ? renderPopover()
            : renderMainValue(item)
        }
        {renderName(item)}
      </div>
      {item.benchmarkValue !== undefined && renderSeparator()}
      {item.benchmarkValue !== undefined && renderBenchmarkValue(item)}
    </div>
  );

  if (isLoading) {
    return (
      <div className="reporting-statistics-table-with-benchmark__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  if (isEmpty(statisticsItems)) {
    return (<EmptyMessage wrapperClass="reporting-statistics-table-with-benchmark__empty-message" />);
  }

  return (
    <div className={containerClassNames}>
      {statisticsItems.map(renderItem)}
    </div>
  );
};

export default ReportingStatisticsTableWithBenchmark;
